/**
 * `useResource` — the standard async-resource hook for dink-admin (DNKADM-0007).
 *
 * Loads a single GET-backed resource and exposes a small, screen-agnostic state
 * machine: `{ data, status, error, refetch }`. Every later screen (Connections,
 * Membership/credits, Custom fields, Policies, …) loads through this, so it is
 * deliberately generic over the payload type and carries no API/contract
 * knowledge — the caller passes a fetcher that returns `Promise<T>`.
 *
 * Behavior:
 *  - Runs the fetcher on mount and whenever the `deps` identity changes (the same
 *    contract as `useEffect` deps; pass the values the fetcher closes over).
 *  - Exposes a STABLE `refetch()` for after-write refresh — its identity does not
 *    change across renders, so it is safe to call from event handlers / effects
 *    without re-triggering them.
 *  - Guards against setting state after unmount and against a stale in-flight
 *    fetch overwriting a newer one (last-call-wins via a per-run token), so a
 *    rapid refetch or a dep change never lands an out-of-date result.
 *
 * `ApiError` (or any thrown error) from the fetcher lands in `error` with
 * `status: 'error'`; the caller maps it to its own UI (inline 400, 502 toast, …).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type ResourceStatus = 'loading' | 'ready' | 'error';

export interface Resource<T> {
  /** The loaded payload, or `null` until the first successful load. */
  data: T | null;
  /** Coarse load state the UI switches on. */
  status: ResourceStatus;
  /** The error from the most recent failed load, else `null`. */
  error: unknown;
  /** Stable manual re-run (e.g. refresh after a write). */
  refetch: () => void;
}

/**
 * Load `fetcher()` and track its state. `deps` mirrors a `useEffect` dependency
 * array: the fetch re-runs when their identity changes. Omit (defaults to `[]`)
 * for a once-on-mount load with manual `refetch`.
 */
export function useResource<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<ResourceStatus>('loading');
  const [error, setError] = useState<unknown>(null);

  // Keep the latest fetcher in a ref so `refetch`'s identity stays stable even
  // as the caller passes a fresh closure each render. The dep array (not the
  // fetcher identity) drives automatic re-runs.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const mountedRef = useRef(true);
  // Monotonic token: only the most recently started run may commit its result,
  // so a stale in-flight fetch can never overwrite a newer one (last-call-wins).
  const runIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setStatus('loading');
    setError(null);
    void (async () => {
      try {
        const result = await fetcherRef.current();
        if (!mountedRef.current || runIdRef.current !== runId) return;
        setData(result);
        setStatus('ready');
      } catch (err) {
        if (!mountedRef.current || runIdRef.current !== runId) return;
        setError(err);
        setStatus('error');
      }
    })();
  }, []);

  useEffect(() => {
    load();
    // `load` is stable; the caller's `deps` drive re-runs (same contract as a
    // raw useEffect dependency array).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, status, error, refetch: load };
}
